import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Footer as CardFooter,
  Header as CardHeader,
  Heading as CardTitle,
  TextField as Input,
  LinkButton,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@react-spectrum/s2'
import {
  ArrowLeft,
  CreditCard,
  Mail,
  ShieldCheck,
  UserRound,
} from 'lucide-react'
import { useState } from 'react'
import { APP_ROUTES } from '@shared/app-routes'
import { trackAnalyticsEvent } from '@client/analytics'
import { getPlanSummary } from '@rpc/plans.rpc'
import { getSubscriptionStatus } from '@rpc/billing.rpc'
import { requestEmailChange, updateDisplayName } from '@rpc/user.rpc'
import { getAuthSession } from '@rpc/auth-session.rpc'
import { getPersonaProfile, updatePersona } from '@rpc/persona.rpc'
import { PERSONA_OPTIONS, type PersonaId } from '@shared/persona'
import { fullWidthStyles, settingsTabListStyles } from '@/styles/s2-macros'

export const Route = createFileRoute('/dashboard/settings')({
  loader: async () => {
    const [session, subscription, personaProfile] = await Promise.all([
      getAuthSession(),
      getSubscriptionStatus(),
      getPersonaProfile(),
    ])
    if (!subscription.hasSubscription || !subscription.planId) {
      return {
        session,
        subscription,
        subscriptionPlan: null,
        personaProfile,
      }
    }

    const subscriptionPlan = await getPlanSummary({
      data: { planId: subscription.planId },
    })
    return { session, subscription, subscriptionPlan, personaProfile }
  },
  component: SettingsPage,
})

function SettingsPage() {
  const { session, subscription, subscriptionPlan, personaProfile } =
    Route.useLoaderData()
  const navigate = useNavigate()
  const user = session?.user

  const planName = subscriptionPlan?.name || 'Free plan'
  const subscriptionStatusLabel = getStatusLabel(subscription.status)
  const subscriptionStatusSummary = getStatusSummary(subscription.status)
  const subscriptionStatusClassName = getStatusClassName(subscription.status)
  const [displayName, setDisplayName] = useState(user?.name ?? '')
  const [savedName, setSavedName] = useState(user?.name ?? '')
  const [emailAddress, setEmailAddress] = useState(user?.email ?? '')
  const [isSavingName, setIsSavingName] = useState(false)
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState(false)
  const [persona, setPersona] = useState<PersonaId | ''>(
    personaProfile?.persona ?? '',
  )
  const [savedPersona, setSavedPersona] = useState<PersonaId | null>(
    personaProfile?.persona ?? null,
  )
  const [isSavingPersona, setIsSavingPersona] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)

  const handleDisplayNameSave = async () => {
    setProfileError(null)
    setProfileSuccess(null)
    setIsSavingName(true)
    trackAnalyticsEvent('settings_display_name_submitted', {
      changed: displayName.trim() !== savedName,
    })
    try {
      const trimmedName = displayName.trim()
      await updateDisplayName({
        data: { name: trimmedName },
      })
      setSavedName(trimmedName)
      setDisplayName(trimmedName)
      setProfileSuccess('Display name updated successfully.')
      trackAnalyticsEvent('settings_display_name_succeeded')
    } catch (error) {
      setProfileError(
        error instanceof Error
          ? error.message
          : 'Failed to update display name. Please try again.',
      )
      trackAnalyticsEvent('settings_display_name_failed')
    } finally {
      setIsSavingName(false)
    }
  }

  const handleEmailChangeRequest = async () => {
    setProfileError(null)
    setProfileSuccess(null)
    setIsRequestingEmailChange(true)
    trackAnalyticsEvent('settings_email_change_submitted', {
      changed:
        emailAddress.trim().toLowerCase() !== (user?.email ?? '').toLowerCase(),
    })
    try {
      await requestEmailChange({
        data: { email: emailAddress.trim() },
      })
      setProfileSuccess(
        'Confirmation sent. Check your current email address to approve this change.',
      )
      trackAnalyticsEvent('settings_email_change_succeeded')
    } catch (error) {
      setProfileError(
        error instanceof Error
          ? error.message
          : 'Failed to request email change. Please try again.',
      )
      trackAnalyticsEvent('settings_email_change_failed')
    } finally {
      setIsRequestingEmailChange(false)
    }
  }

  const handlePersonaSave = async () => {
    setProfileError(null)
    setProfileSuccess(null)
    setIsSavingPersona(true)
    try {
      await updatePersona({
        data: { persona: persona as PersonaId, source: 'settings' },
      })
      setSavedPersona(persona as PersonaId)
      setProfileSuccess('Main role updated successfully.')
    } catch (error) {
      setProfileError(
        error instanceof Error
          ? error.message
          : 'Failed to update your main role. Please try again.',
      )
    } finally {
      setIsSavingPersona(false)
    }
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <section className="rounded-[2rem] border border-border/70 bg-gradient-to-br from-white via-white to-sky-50/70 p-6 shadow-[0_24px_80px_-40px_rgba(15,23,42,0.3)] dark:from-neutral-950 dark:via-neutral-950 dark:to-sky-950/20 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary dark:bg-neutral-950/60">
                <ShieldCheck className="h-3.5 w-3.5" />
                Account control
              </div>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
                  Settings
                </h1>
                <p className="max-w-2xl text-sm leading-6 text-neutral-600 dark:text-neutral-300">
                  Keep your profile current, manage email changes, and check
                  your subscription without leaving the product.
                </p>
              </div>
            </div>
            <LinkButton
              href={APP_ROUTES.dashboard.home}
              variant="secondary"
              fillStyle="outline"
              onPress={() =>
                trackAnalyticsEvent('navigation_clicked', {
                  destination: APP_ROUTES.dashboard.home,
                  label: 'back_to_dashboard',
                  location: 'settings',
                })
              }
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to dashboard
            </LinkButton>
          </div>
        </section>

        <Tabs
          aria-label="Settings sections"
          defaultSelectedKey="profile"
          onSelectionChange={(key) =>
            trackAnalyticsEvent('settings_tab_selected', {
              tab: String(key),
            })
          }
          styles={fullWidthStyles}
        >
          <TabList
            aria-label="Settings sections"
            styles={settingsTabListStyles}
          >
            <Tab id="profile">Profile</Tab>
            <Tab id="subscription">Subscription</Tab>
          </TabList>

          <TabPanel id="profile">
            <div className="grid w-full gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <Card styles={fullWidthStyles}>
                <CardHeader>
                  <CardTitle>Profile</CardTitle>
                  <CardDescription>
                    Your account identity inside Building Regs.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-gradient-to-br from-neutral-950 to-neutral-800 p-5 text-white">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/12">
                          <UserRound className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold">
                            {savedName || 'Unnamed user'}
                          </p>
                          <p className="truncate text-sm text-white/70">
                            {user?.email ?? 'No email on file'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <div className="rounded-2xl border border-border/80 bg-background px-4 py-3">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
                            <ShieldCheck className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              Public account name
                            </p>
                            <p className="mt-1 text-sm leading-6 text-muted-foreground">
                              Your display name appears anywhere your account is
                              surfaced in the product workspace.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/80 bg-background px-4 py-3">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
                            <Mail className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              Confirmed email changes
                            </p>
                            <p className="mt-1 text-sm leading-6 text-muted-foreground">
                              Email updates are protected. You must confirm the
                              request from your current inbox before the change
                              takes effect.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card styles={fullWidthStyles}>
                <CardHeader>
                  <CardTitle>Personal information</CardTitle>
                  <CardDescription>
                    Update your display name and email address.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {profileError ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/20 dark:text-red-400">
                        {profileError}
                      </div>
                    ) : null}
                    {profileSuccess ? (
                      <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/30 dark:bg-green-900/20 dark:text-green-400">
                        {profileSuccess}
                      </div>
                    ) : null}
                    <div className="space-y-3 rounded-2xl border border-border/80 bg-background p-4">
                      <label
                        htmlFor="name"
                        className="text-sm font-medium text-foreground"
                      >
                        Display name
                      </label>
                      <Input
                        id="name"
                        name="displayName"
                        autoComplete="name"
                        aria-label="Display name"
                        value={displayName}
                        onChange={setDisplayName}
                        isDisabled={isSavingName}
                      />
                      <p className="text-xs leading-5 text-muted-foreground">
                        This is shown in your account and workspace.
                      </p>
                      <div>
                        <Button
                          type="button"
                          onPress={handleDisplayNameSave}
                          isDisabled={
                            isSavingName ||
                            displayName.trim().length < 2 ||
                            displayName.trim() === savedName
                          }
                        >
                          {isSavingName ? 'Saving...' : 'Save display name'}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-3 rounded-2xl border border-border/80 bg-background p-4">
                      <label
                        htmlFor="email"
                        className="text-sm font-medium text-foreground"
                      >
                        Email address
                      </label>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        aria-label="Email address"
                        value={emailAddress}
                        onChange={setEmailAddress}
                        isDisabled={isRequestingEmailChange}
                      />
                      <p className="text-xs leading-5 text-muted-foreground">
                        Changing your email requires confirmation from your
                        current email address first.
                      </p>
                      <div>
                        <Button
                          type="button"
                          variant="secondary"
                          fillStyle="outline"
                          onPress={handleEmailChangeRequest}
                          isDisabled={
                            isRequestingEmailChange ||
                            emailAddress.trim().length === 0 ||
                            emailAddress.trim().toLowerCase() ===
                              (user?.email ?? '').toLowerCase()
                          }
                        >
                          {isRequestingEmailChange
                            ? 'Sending confirmation...'
                            : 'Change email address'}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-3 rounded-2xl border border-border/80 bg-background p-4">
                      <label
                        htmlFor="main-role"
                        className="text-sm font-medium text-foreground"
                      >
                        Main role
                      </label>
                      <select
                        id="main-role"
                        name="persona"
                        aria-label="Main role"
                        className="min-h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                        value={persona}
                        disabled={isSavingPersona}
                        onChange={(event) =>
                          setPersona(event.target.value as PersonaId | '')
                        }
                      >
                        <option value="" disabled>
                          Select your main role
                        </option>
                        {PERSONA_OPTIONS.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs leading-5 text-muted-foreground">
                        Used to understand which professionals get the most
                        value from BuildingRegs. You can change this at any
                        time.
                      </p>
                      <div>
                        <Button
                          type="button"
                          onPress={handlePersonaSave}
                          isDisabled={
                            isSavingPersona ||
                            !persona ||
                            persona === savedPersona
                          }
                        >
                          {isSavingPersona ? 'Saving...' : 'Save main role'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabPanel>

          <TabPanel id="subscription">
            <div className="grid w-full gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              <Card styles={fullWidthStyles}>
                <CardHeader>
                  <CardTitle>Subscription plan</CardTitle>
                  <CardDescription>
                    Manage your billing and plan details.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    <div className="rounded-[1.6rem] border border-border/80 bg-gradient-to-br from-sky-50 via-white to-cyan-50 p-5 dark:from-sky-950/20 dark:via-neutral-950 dark:to-cyan-950/20">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                            Active access
                          </p>
                          <p className="text-2xl font-semibold tracking-tight text-foreground">
                            {planName}
                          </p>
                          <p className="text-sm leading-6 text-muted-foreground">
                            {subscription.hasSubscription &&
                            subscription.currentPeriodEnd
                              ? `Renews on ${new Date(
                                  subscription.currentPeriodEnd,
                                ).toLocaleDateString()}`
                              : 'You are currently on the free plan.'}
                          </p>
                        </div>
                        <div
                          className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${subscriptionStatusClassName}`}
                        >
                          {subscriptionStatusLabel}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          Billing state
                        </p>
                        <p className="mt-2 text-base font-semibold text-foreground">
                          {subscriptionStatusSummary}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Review whether the account is fully active, awaiting
                          payment, or needs billing attention.
                        </p>
                      </div>
                      <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          Renewal date
                        </p>
                        <p className="mt-2 text-base font-semibold text-foreground">
                          {subscription.hasSubscription &&
                          subscription.currentPeriodEnd
                            ? new Date(
                                subscription.currentPeriodEnd,
                              ).toLocaleDateString()
                            : 'No renewal scheduled'}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          The next point at which billing or plan status can
                          change.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <div className="flex w-full justify-end">
                    {subscription.hasSubscription ? (
                      <Button
                        onPress={() => {
                          trackAnalyticsEvent(
                            'settings_subscription_action_clicked',
                            {
                              action: 'manage_subscription',
                              has_subscription: true,
                            },
                          )
                          navigate({ to: APP_ROUTES.dashboard.subscription })
                        }}
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Manage subscription
                      </Button>
                    ) : (
                      <Button
                        onPress={() => {
                          trackAnalyticsEvent(
                            'settings_subscription_action_clicked',
                            {
                              action: 'upgrade_plan',
                              has_subscription: false,
                            },
                          )
                          navigate({ to: APP_ROUTES.pricing })
                        }}
                      >
                        Upgrade plan
                      </Button>
                    )}
                  </div>
                </CardFooter>
              </Card>

              <Card styles={fullWidthStyles}>
                <CardHeader>
                  <CardTitle>What this page controls</CardTitle>
                  <CardDescription>
                    Use this section when you need to update billing access.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                      <p className="text-sm font-semibold text-foreground">
                        Plan and billing access
                      </p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        Review the current plan, move to billing management, or
                        upgrade when you need more usage or team access.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                      <p className="text-sm font-semibold text-foreground">
                        Renewal visibility
                      </p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        Keep track of the renewal date so changes to access or
                        cost are predictable.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabPanel>
        </Tabs>
      </div>
    </div>
  )
}

function getStatusLabel(status: string | undefined) {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'Active'
    case 'past_due':
      return 'Payment issue'
    case 'incomplete':
      return 'Payment pending'
    case 'canceled':
      return 'Cancelled'
    default:
      return 'Active'
  }
}

function getStatusClassName(status: string | undefined) {
  switch (status) {
    case 'past_due':
      return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-500'
    case 'incomplete':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-500'
    case 'canceled':
      return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
    default:
      return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-500'
  }
}

function getStatusSummary(status: string | undefined) {
  switch (status) {
    case 'past_due':
      return 'Needs billing attention'
    case 'incomplete':
      return 'Awaiting payment action'
    case 'canceled':
      return 'Ended access'
    default:
      return 'In good standing'
  }
}
